import { createBrowserRouter, Navigate, Outlet } from 'react-router-dom';
import { ShellLayout } from './layouts/ShellLayout';
import { PanelHost } from './panels/PanelHost';
import { TOP_NAVS, type TopNavId } from './layouts/navigation';
import { sidebarFor } from './panels/registry';
import Login from './pages/Login';
import ChangePassword from './pages/ChangePassword';
import { RequireAuth } from './auth/RequireAuth';
import { RequireNav, DefaultLanding } from './auth/RequireNav';
import { useAuth } from './auth/AuthContext';
import { visibleSidebar } from './auth/panelAccess';

/// A section's index: redirect to the first panel THIS role may see. Static
/// `sidebar[0]` would send e.g. a maker user to a Quant panel under `trader`;
/// this picks the first role-visible one instead.
function SectionIndex({ nav }: { nav: TopNavId }) {
  const { role } = useAuth();
  const visible = visibleSidebar(sidebarFor(nav), role);
  if (visible[0]) {
    return <Navigate to={visible[0].id} replace />;
  }
  return <PanelHost panelId={`${nav}.empty`} />;
}

const sectionRoutes = TOP_NAVS.map<{
  path: string;
  element: JSX.Element;
  children: { index?: boolean; path?: string; element: JSX.Element }[];
}>((nav) => {
  const sidebar = sidebarFor(nav.id as TopNavId);
  return {
    path: nav.id,
    // Role guard for the whole section: a role that can't see this nav is
    // redirected to its own landing page.
    element: (
      <RequireNav nav={nav.id as TopNavId}>
        <Outlet />
      </RequireNav>
    ),
    children: [
      {
        index: true,
        element: <SectionIndex nav={nav.id as TopNavId} />,
      },
      ...sidebar.map((item) => ({
        path: item.id,
        element: <PanelHost panelId={`${nav.id}.${item.id}`} />,
      })),
    ],
  };
});

export const router = createBrowserRouter([
  { path: '/login', element: <Login /> },
  {
    path: '/',
    element: (
      <RequireAuth>
        <ShellLayout />
      </RequireAuth>
    ),
    children: [
      // Role-aware landing (user → /signals, others → /home).
      { index: true, element: <DefaultLanding /> },
      // Account pages sit outside the role-gated sections: every signed-in
      // user can change their own password regardless of role.
      { path: 'account/password', element: <ChangePassword /> },
      ...sectionRoutes,
      { path: '*', element: <DefaultLanding /> },
    ],
  },
]);
